<template>
  <b-row>
    <b-col sm="12">

      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
          </div>
        </section>
      </b-card>

      <div class="reports-design-workspace">
        <div class="reports-design-selector-panel">
          <b-row>
            <b-col md="4">
              <FindObject render="search"
                          type="reports"
                          @select-object="loadReports($event)"
                          :valueID="crud.form.id"
                          :extra-filter-data="{ modules: activeModuleIds }"/>
            </b-col>

            <b-col md="1" v-if="crud.form.reports && !crud.form.reports.only_detail">
              <b-form-group label="ID" v-if="canEditEscpos || (contentRender!='[[no-config]]' && contentRender!='[[no-report]]')">
                <AppTextInput type="number"
                              size="sm"
                              v-model="filter.id"
                              :allowed-pattern="/[0-9]/g"
                              required
                              placeholder="Ingresar un ID"
                              @keyup.enter="filterReports()" />
              </b-form-group>
            </b-col>
            <b-col md="1" v-if="canEditEscpos || (contentRender!='[[no-config]]' && contentRender!='[[no-report]]')">
              <b-button variant="outline-dark"
                        class="reports-design-button-filter"
                        size="sm"
                        v-b-tooltip.hover
                        title="Aplicar filtro"
                        @click="filterReports()">
                <AppIcon name="filter" class="mr-1" />Filtrar
              </b-button>
            </b-col>
          </b-row>
        </div>

        <b-card v-if="!crud.form.reports" class="reports-design-empty-card">
          <EmptyState icon="search" title="Buscá y seleccioná un reporte" description="Elegí un reporte para configurar sus variables, diseño, configuración y vista previa." />
        </b-card>

        <b-row v-else class="reports-design-layout">
          <b-col lg="3">     
            <b-tabs>
              <b-tab title="Disponibles" active>
                <b-row>           
                  <b-col md="12">                                                  
                    <JsonAccordion v-if="jsonFilter" :data="jsonFilter" class="reports-design-pre-json" />
                    <EmptyState v-else icon="braces" title="No hay datos para mostrar" description="Cargá un ID y filtrá para ver las variables disponibles del registro." />
                  </b-col>
                </b-row>
              </b-tab>
              <b-tab title="Utilizados">
                <div class="reports-design-used-accordion">
                  <b-card no-body class="reports-design-used-card" v-if="canEditPdf">
                    <b-button block variant="light" class="reports-design-used-header" v-b-toggle.reports-used-pdf>
                      <AppIcon name="file-earmark-text" /> PDF
                    </b-button>
                    <b-collapse id="reports-used-pdf" accordion="reports-used" :visible="canEditPdf && !canEditEscpos">
                      <b-card-body>
                        <pre v-if="variablesUsedPdf" class="reports-design-pre-json reports-design-json-code" v-html="formatJson(variablesUsedPdf)"></pre>
                        <EmptyState v-else icon="file-earmark-text" title="Aún no se utilizó ninguna variable" description="Las variables que uses en el diseño PDF van a aparecer acá." />
                      </b-card-body>
                    </b-collapse>
                  </b-card>

                  <EscposUsedAccordion :show="canEditEscpos" :open="!canEditPdf" :data="variablesUsedEscpos" />

                </div>
              </b-tab>
              <b-tab title="Ayuda">
                <div class="reports-design-help" role="tablist">

                  <div class="reports-design-help-section">
                    <div class="reports-design-help-title reports-design-help-toggle" v-b-toggle.help-variables>
                      <AppIcon name="braces" /> Variables
                      <AppIcon name="chevron-down" class="reports-design-help-chevron" />
                    </div>
                    <b-collapse id="help-variables" accordion="help-accordion">
                      <div class="reports-design-help-item">
                        <code v-pre>{{variable}}</code>
                        <span>Insertar una variable escribiendo el nombre entre dos llaves.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>{{customer.name}}</code>
                        <span>Acceder a propiedades hijas usando <strong>punto</strong> hasta la última propiedad disponible.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>{{customerName}}</code>
                        <span>Si el nombre exacto no existe en los datos, el sistema reintenta automáticamente su versión <strong>snake_case</strong> (en este ejemplo, <code v-pre>customer_name</code>) antes de darlo por vacío.</span>
                      </div>
                      <div class="reports-design-help-note">
                        Dejá un espacio antes de la llave de apertura y después de la llave de cierre cuando lo uses dentro del template.<br>
                        Si el campo no existe (ni en su forma original ni en snake_case), la variable se reemplaza por texto vacío: no genera error ni dato roto en el PDF.
                      </div>
                    </b-collapse>
                  </div>

                  <div class="reports-design-help-section">
                    <div class="reports-design-help-title reports-design-help-toggle" v-b-toggle.help-details>
                      <AppIcon name="list-nested" /> Detalles
                      <AppIcon name="chevron-down" class="reports-design-help-chevron" />
                    </div>
                    <b-collapse id="help-details" accordion="help-accordion">
                      <div class="reports-design-help-item">
                        <code v-pre>{{details|arrayStart}}</code>
                        <span>Iniciar un bloque repetible usando la colección de detalles.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-text="'{{-|arrayStart}}'"></code>
                        <span>Usar cuando el dato raíz ya es una lista.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>{{details|arrayEnd}}</code>
                        <span>Cerrar el bloque repetible.</span>
                      </div>
                      <div class="reports-design-help-note">
                        Dentro del bloque repetible, las variables parten desde la primera propiedad hija declarada en la repetición.
                      </div>
                    </b-collapse>
                  </div>

                  <div class="reports-design-help-section">
                    <div class="reports-design-help-title reports-design-help-toggle" v-b-toggle.help-formats>
                      <AppIcon name="filter" /> Formatos
                      <AppIcon name="chevron-down" class="reports-design-help-chevron" />
                    </div>
                    <b-collapse id="help-formats" accordion="help-accordion">
                      <div class="reports-design-help-item">
                        <code v-pre>{{date|date}}</code>
                        <span>Aplicar un formato agregando <strong>|</strong> después del nombre de la variable.</span>
                      </div>
                      <div class="reports-design-help-format-list">
                        <div class="reports-design-help-format">
                          <code>date</code>
                          <span>Fechas.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>currency</code>
                          <span>Importes monetarios.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>int</code>
                          <span>Números enteros.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>decimal</code>
                          <span>Números decimales.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>pointSales</code>
                          <span>Punto de venta.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>numberInvoice</code>
                          <span>Número de comprobante.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>si-no</code>
                          <span>Valores booleanos.</span>
                        </div>
                        <div class="reports-design-help-format">
                          <code>image</code>
                          <span>Renderizado de imágenes.</span>
                        </div>
                      </div>
                      <div class="reports-design-help-subtitle">Formatos avanzados</div>
                      <div class="reports-design-help-item">
                        <code>concatLeft[valor]</code>
                        <span>Concatena texto a la izquierda.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code>concatRight[valor]</code>
                        <span>Concatena texto a la derecha.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code v-pre>{{price_unit|eval(field['price_unit']+(field['amount_iva']/field['quantity']))}}</code>
                        <span><strong>eval</strong> ejecuta cálculos. Usá <strong>field['campo']</strong> para leer valores del registro y evitá espacios en la fórmula.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code>if(texto,_,etiqueta,clase)</code>
                        <span>Si el campo tiene un valor verdadero (no vacío, no 0), muestra <strong>texto</strong> envuelto en <strong>etiqueta</strong> (con <strong>clase</strong> opcional); si es falso, no muestra nada. El segundo parámetro no se utiliza actualmente: dejalo vacío, ej. <code v-pre>if(Activo,,span,text-success)</code>.</span>
                      </div>
                      <div class="reports-design-help-item">
                        <code>field-valid(valor,campoSiTrue,campoSiFalse,formato,etiqueta,clase)</code>
                        <span>Compara el valor del campo actual contra <strong>valor</strong>: si coincide, muestra el campo <strong>campoSiTrue</strong>; si no, muestra <strong>campoSiFalse</strong>. Ambos se envuelven en <strong>etiqueta</strong> (con <strong>clase</strong> opcional). <strong>formato</strong> es opcional: <code>default</code> (texto tal cual), <code>currency</code> o <code>currency-int</code> (con símbolo de moneda). Si el campo actual está vacío, no muestra nada.</span>
                      </div>
                      <div class="reports-design-help-note">
                        En <strong>if</strong> y <strong>field-valid</strong> no dejes espacios después de las comas: se toman literales y rompen la etiqueta o la clase resultante.
                      </div>
                    </b-collapse>
                  </div>

                  <EscposHelpAccordion :show="canEditEscpos" />

                  <div class="reports-design-help-section">
                    <div class="reports-design-help-title reports-design-help-toggle" v-b-toggle.help-complex-formats>
                      <AppIcon name="braces" /> Formatos complejos
                      <AppIcon name="chevron-down" class="reports-design-help-chevron" />
                    </div>
                    <b-collapse id="help-complex-formats" accordion="help-accordion">
                      <div class="reports-design-help-mini">
                        <button type="button" class="reports-design-help-mini__title" v-b-toggle.help-complex-price-list>Lista de Precios <AppIcon name="chevron-down" /></button>
                        <b-collapse id="help-complex-price-list" accordion="help-complex-accordion"><div class="reports-design-help-item"><code v-pre>{{products|tablePriceList['code:15,name:50,desc:10,price:15,days_update:10']}}</code><span>Tabla con encabezado. Cada campo se define como <strong>campo:ancho</strong>.</span></div><div class="reports-design-help-item"><code>tablePriceListWithoutHeader[&lt;fields&gt;]</code><span>La misma tabla, sin encabezado.</span></div><div class="reports-design-help-note">Campos disponibles: <strong>code, name, desc, price, days_update</strong>. Quitá un campo del tag para ocultarlo en el PDF.</div></b-collapse>
                      </div>
                      <div class="reports-design-help-mini">
                        <button type="button" class="reports-design-help-mini__title" v-b-toggle.help-complex-sales-iva>IVA Ventas <AppIcon name="chevron-down" /></button>
                        <b-collapse id="help-complex-sales-iva" accordion="help-complex-accordion"><div class="reports-design-help-item"><code v-pre>{{table|tableSalesIva['business:8,voucher:14,date:7,date_expiration:7,customer:12,amount_net:9,ivas:8,amount_total:9']}}</code><span>Definí las columnas y sus anchos directamente en el HTML.</span></div><div class="reports-design-help-note">Campos fijos: <strong>business, voucher, date, date_expiration, customer, amount_net, amount_total</strong>. <strong>ivas</strong> agrega todas las alícuotas; usá <strong>iva_ID</strong> para una alícuota puntual. La preview toma los últimos 10 comprobantes y el PDF usa los filtros de IVA Ventas.</div></b-collapse>
                      </div>
                      <div class="reports-design-help-mini">
                        <button type="button" class="reports-design-help-mini__title" v-b-toggle.help-complex-purchase-iva>IVA Compras <AppIcon name="chevron-down" /></button>
                        <b-collapse id="help-complex-purchase-iva" accordion="help-complex-accordion"><div class="reports-design-help-item"><code v-pre>{{table|tablePurchaseIva['business:8,voucher:10,date:6,date_expiration:6,date_imputation:6,supplier:10,amount_net:8,ivas:7,perceptions:7,amount_total:8']}}</code><span>Definí las columnas y sus anchos directamente en el HTML.</span></div><div class="reports-design-help-note">Campos fijos: <strong>business, voucher, date, date_expiration, date_imputation, supplier, amount_net, amount_total</strong>. <strong>ivas</strong> agrega todas las alícuotas, <strong>perceptions</strong> todas las percepciones y <strong>iva_ID</strong> una alícuota puntual. La preview toma los últimos 10 comprobantes y el PDF usa los filtros de IVA Compras.</div></b-collapse>
                      </div>
                      <div class="reports-design-help-mini">
                        <button type="button" class="reports-design-help-mini__title" v-b-toggle.help-complex-current-account>Cuenta Corriente de Ventas <AppIcon name="chevron-down" /></button>
                        <b-collapse id="help-complex-current-account" accordion="help-complex-accordion"><div class="reports-design-help-item"><code v-pre>{{table|tableSalesCurrentAccount}}</code><span>Muestra los movimientos de la vista elegida: Por Factura, Pendiente o Histórica.</span></div><div class="reports-design-help-note">Incluye fecha, comprobante, vencimiento, debe, haber y acumulado. La impresión toma el cliente y período seleccionados en Cuenta Corriente de Ventas.</div></b-collapse>
                      </div>
                      <div class="reports-design-help-mini">
                        <button type="button" class="reports-design-help-mini__title" v-b-toggle.help-complex-purchase-current-account>Cuenta Corriente de Compras <AppIcon name="chevron-down" /></button>
                        <b-collapse id="help-complex-purchase-current-account" accordion="help-complex-accordion"><div class="reports-design-help-item"><code v-pre>{{table|tablePurchaseCurrentAccount}}</code><span>Muestra los movimientos de la vista elegida: Por Factura, Pendiente o Histórica.</span></div><div class="reports-design-help-note">Incluye fecha, comprobante, vencimiento, debe, haber y acumulado. La impresión toma el proveedor y período seleccionados en Cuenta Corriente de Compras.</div></b-collapse>
                      </div>
                    </b-collapse>
                  </div>

                </div>
              </b-tab>                
            </b-tabs>
          </b-col>

          <b-col lg="9">
            <b-tabs>
              <b-tab title="Diseño" active>
                <div class="reports-design-accordion">
                  <b-card no-body class="reports-design-accordion-card" key="design-pdf" v-if="canEditPdf">
                    <div class="reports-design-accordion-header" v-b-toggle.reports-design-pdf>
                      <AppIcon name="file-earmark-text" />PDF
                      <AppIcon name="chevron-down" class="reports-design-accordion-chevron" />
                    </div>
                    <b-collapse id="reports-design-pdf" accordion="reports-design-format" :visible="canEditPdf && !canEditEscpos">
                      <b-card-body>
                        <b-row>
                          <b-col lg="12" class="mb-2">
                            <b-button variant="warning" class="pull-left" size="sm" @click="restoreDefaultReports('pdf')" v-if="!crud.form.blocked && hasDefaultDesign('pdf')">
                              <AppIcon name="arrow-counterclockwise" class="mr-1" />Restaurar PDF
                            </b-button>
                            <b-button variant="dark" class="pull-right" size="sm" @click="saveReports('pdf')">
                              <AppIcon name="save" class="mr-1" />Guardar PDF
                            </b-button>
                          </b-col>
                        </b-row>
                        <prism-editor v-model="crud.form.content" language="html" class="crud-reports-design-code-editor"></prism-editor>
                      </b-card-body>
                    </b-collapse>
                  </b-card>

                  <EscposReportDesigner v-if="canEditEscpos"
                                        :report="crud.form.reports"
                                        :report-id="crud.form.id"
                                        :start-open="!canEditPdf"
                                        :show-design="true"
                                        :show-config="false"
                                        :show-preview="false"
                                        @saved="reloadSelectedReport"
                                        @content-escpos="contentEscpos = $event" />
                </div>
              </b-tab>

              <b-tab title="Configuraciones">
                <div class="reports-design-accordion">
                  <b-card no-body class="reports-design-accordion-card" key="config-pdf" v-if="canEditPdf">
                    <div class="reports-design-accordion-header" v-b-toggle.reports-config-pdf>
                      <AppIcon name="file-earmark-text" />PDF
                      <AppIcon name="chevron-down" class="reports-design-accordion-chevron" />
                    </div>
                    <b-collapse id="reports-config-pdf" accordion="reports-config-format" :visible="canEditPdf && !isLabelReport && !canEditEscpos">
                      <b-card-body>
                        <b-row>
                          <b-col md="4">
                            <b-form-group label="Medidas">
                              <b-form-select v-model="crud.form.page"
                                             :options="arr.page"
                                             :disabled="!pageSizesLoaded">
                                <template v-slot:first v-if="!pageSizesLoaded">
                                  <b-form-select-option :value="crud.form.page" disabled>Cargando…</b-form-select-option>
                                </template>
                              </b-form-select>
                            </b-form-group>
                          </b-col>
                          <b-col md="4">
                            <b-form-group label="Orientación">
                              <b-form-select v-model="crud.form.orientation" :options="arr.orientation"></b-form-select>
                            </b-form-group>
                          </b-col>
                          <b-col md="4">
                            <b-form-group label="Fuente por Defecto">
                              <b-form-select v-model="crud.form.font" :options="arr.fonts"></b-form-select>
                            </b-form-group>
                          </b-col>
                          <b-col md="12">
                            <b-form-checkbox v-model="crud.form.blocked"
                                              switch
                                              size="sm"
                                              class="pull-left">
                              Bloquea la configuración del reporte
                            </b-form-checkbox>
                          </b-col>
                          <b-col lg="12" class="mt-3">
                            <b-button variant="dark" class="pull-right" size="sm" @click="saveReportsConfiguration('pdf')">
                              <AppIcon name="save" class="mr-1" />Guardar PDF
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-card-body>
                    </b-collapse>
                  </b-card>

                  <EscposReportDesigner v-if="canEditEscpos"
                                        :start-open="!canEditPdf"
                                        :show-preview="false"
                                        :report-id="crud.form.id"
                                        :report="crud.form.reports"
                                        :filter-id="filter.id"
                                        :json-data="jsonFilter"
                                        @saved="reloadSelectedReport" />

                  <LabelReportDesigner v-if="isLabelReport"
                                     :report="crud.form.reports"
                                     :page="crud.form.page"
                                     :orientation="crud.form.orientation"
                                     :page-sizes-mm="pageSizesMm"
                                     :page-sizes-loaded="pageSizesLoaded"
                                     :start-open="!canEditEscpos"
                                     :show-config="true"
                                     :show-preview="false"
                                     @saved="reloadSelectedReport" />

                  <b-card no-body class="reports-design-accordion-card" key="config-general" v-if="crud.form.reports">
                    <div class="reports-design-accordion-header" v-b-toggle.reports-config-general>
                      <AppIcon name="settings" />Generales
                      <AppIcon name="chevron-down" class="reports-design-accordion-chevron" />
                    </div>
                    <b-collapse id="reports-config-general" accordion="reports-config-format" :visible="isVersionAlternative">
                      <b-card-body>
                        <b-row>
                          <b-col md="8">
                            <b-form-group label="Nombre">
                              <b-form-input type="text"
                                            size="sm"
                                            v-model="crud.form.name"
                                            required
                                            placeholder="Ingresar un nombre">
                              </b-form-input>
                            </b-form-group>
                          </b-col>
                          <b-col md="12" v-if="isVersionAlternative">
                            <b-form-group label="Activo">
                              <b-form-checkbox v-model="crud.form.active"
                                                switch
                                                size="sm"
                                                class="pull-left">
                                Disponible para la búsqueda de reportes en la transacción correspondiente
                              </b-form-checkbox>
                            </b-form-group>
                          </b-col>
                          <b-col lg="12" class="mt-3">
                            <b-button variant="dark" class="pull-right" size="sm" @click="saveReportsConfiguration('general')">
                              <AppIcon name="save" class="mr-1" />Guardar Generales
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-card-body>
                    </b-collapse>
                  </b-card>
                </div>
              </b-tab>

              <b-tab title="Vista Previa" @click="renderReportsPreview()">
                <div class="reports-design-accordion">
                  <LabelReportDesigner v-if="isLabelReport"
                                       :report="crud.form.reports"
                                       :page="crud.form.page"
                                       :orientation="crud.form.orientation"
                                       :page-sizes-mm="pageSizesMm"
                                       :page-sizes-loaded="pageSizesLoaded"
                                       :show-config="false"
                                       :show-preview="true"
                                       :start-open="!canEditEscpos" />
                  <b-card no-body class="reports-design-accordion-card" key="preview-pdf" v-if="canEditPdf && !isLabelReport">
                    <div class="reports-design-accordion-header" v-b-toggle.reports-preview-pdf @click="renderReports()">
                      <AppIcon name="file-earmark-text" />PDF
                      <AppIcon name="chevron-down" class="reports-design-accordion-chevron" />
                    </div>
                    <b-collapse id="reports-preview-pdf" accordion="reports-preview-format" :visible="canEditPdf && !canEditEscpos">
                      <b-card-body>
                        <b-row>
                          <b-col lg="12" class="mb-2">
                            <b-button variant="outline-dark" class="pull-right" size="sm" @click="generateReports()">
                              <AppIcon name="printer" class="mr-1" />Imprimir PDF
                            </b-button>
                          </b-col>
                          <b-col lg="12">
                            <EmptyState v-if="getFormatContentHTML()=='[[no-config]]'"
                                        icon="code"
                                        title="Reporte sin configurar"
                                        description="Guardá el código fuente para visualizarlo." />
                            <EmptyState v-if="getFormatContentHTML()=='[[no-report]]'"
                                        icon="reports"
                                        title="No se seleccionó el reporte"
                                        description="Seleccioná el reporte a editar." />
                            <EmptyState v-if="getFormatContentHTML()=='[[no-data]]' && isLabelReport"
                                        icon="barcode"
                                        title="No hay productos para previsualizar"
                                        description="La muestra usa productos marcados con «Mostrar en impresión». Marcá alguno o generá las etiquetas desde Productos → Impresión de Etiquetas." />
                            <EmptyState v-if="getFormatContentHTML()=='[[no-data]]' && !isLabelReport && !filter.id"
                                        icon="filter"
                                        title="Cargá un ID y filtrá"
                                        description="Filtrá para visualizar el comprobante con un ID que tenga registros generados." />
                            <EmptyState v-if="getFormatContentHTML()=='[[no-data]]' && !isLabelReport && filter.id"
                                        icon="search"
                                        title="No se encontraron datos"
                                        description="Filtrá por un ID con registros generados." />

                            <div v-if="getFormatContentHTML()!='[[no-config]]' && getFormatContentHTML()!='[[no-report]]' && getFormatContentHTML()!='[[no-data]]'">
                              <div class="crud-reports-design-preview">
                                <!-- El preview va en un iframe aislado: el template del reporte trae
                                     su propio <style> / body / @page y, inyectado con v-html, se
                                     derramaba sobre toda la pantalla de diseño (ej. la regla .label
                                     de products-label pisaba el ícono del acordeón "Etiqueta"). -->
                                <iframe :srcdoc="getFormatContentHTML()"
                                        class="crud-reports-design-preview-html"
                                        sandbox
                                        title="Vista previa del reporte"></iframe>
                              </div>
                            </div>
                          </b-col>
                        </b-row>
                      </b-card-body>
                    </b-collapse>
                  </b-card>

                  <EscposReportDesigner v-if="canEditEscpos"
                                        :start-open="!canEditPdf"
                                        :show-config="false"
                                        :report-id="crud.form.id"
                                        :report="crud.form.reports"
                                        :filter-id="filter.id"
                                        :json-data="jsonFilter" />

                </div>
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>      

      </div>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="chevron-double-left" class="mr-1" />Volver
          </b-button>
        </div>
      </b-card>

      <!--
      ###########################
      ######### PRINTER #########
      ###########################
      -->
      <Printer v-if="pdfPrintReport" :pdf="pdfPrintReport" />

    </b-col>
  </b-row>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import JsonHighlight from './helpers/jsonHighlight' 
  import Session from '@/handler/session'    
  import FindObject from '@/components/inc/find/findObject'
  import Printer from '@/components/inc/printer/printer'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import AppTextInput from '@/components/inc/input/AppTextInput'
  import LabelReportDesigner from './components/labels/LabelReportDesigner'
  import EscposReportDesigner from './components/escpos/EscposReportDesigner'
  import EscposUsedAccordion from './components/escpos/EscposUsedAccordion'
  import EscposHelpAccordion from './components/escpos/EscposHelpAccordion'

  import PrismEditor from 'vue-prism-editor'
  import "prismjs";
  import "prismjs/themes/prism.css";

  const JsonAccordion = {
    name: 'JsonAccordion',
    props: {
      data: {
        required: true,
      },
      label: {
        type: String,
        default: '',
      },
      root: {
        type: Boolean,
        default: true,
      },
    },
    computed: {
      isObject() {
        return this.data !== null && typeof this.data === 'object'
      },
      isArray() {
        return Array.isArray(this.data)
      },
      entries() {
        if(!this.isObject) {
          return []
        }

        return Object.keys(this.data).map((key) => {
          return {
            key: key,
            value: this.data[key],
          }
        })
      },
      summary() {
        if(this.isArray) {
          return this.data.length + ' items'
        }

        if(this.isObject) {
          return this.entries.length + ' campos'
        }

        return ''
      },
      primitiveClass() {
        if(typeof this.data === 'string') {
          return 'reports-json-string'
        }

        if(typeof this.data === 'number') {
          return 'reports-json-number'
        }

        if(typeof this.data === 'boolean') {
          return 'reports-json-boolean'
        }

        if(this.data === null) {
          return 'reports-json-null'
        }

        return ''
      },
      primitiveValue() {
        if(typeof this.data === 'string') {
          return '"' + this.data + '"'
        }

        if(this.data === null) {
          return 'null'
        }

        return String(this.data)
      },
    },
    template: `
      <div class="reports-json-accordion">
        <template v-if="isObject">
          <details class="reports-json-node" :open="root">
            <summary>
              <span v-if="label" class="reports-json-key">{{ label }}</span>
              <span v-else class="reports-json-key">root</span>
              <span class="reports-json-summary">{{ isArray ? 'Array' : 'Object' }} · {{ summary }}</span>
            </summary>
            <div class="reports-json-children">
              <JsonAccordion
                v-for="item in entries"
                :key="item.key"
                :label="item.key"
                :data="item.value"
                :root="false"
              />
            </div>
          </details>
        </template>
        <template v-else>
          <div class="reports-json-leaf">
            <span class="reports-json-key">{{ label }}</span>
            <span class="reports-json-separator">:</span>
            <span :class="primitiveClass">{{ primitiveValue }}</span>
          </div>
        </template>
      </div>
    `,
  }

  export default {
    components: {
      FindObject,
      PrismEditor,
      Printer,
      JsonAccordion,
      TabHeaderActions,
      AppTextInput,
      LabelReportDesigner,
      EscposReportDesigner,
      EscposUsedAccordion,
      EscposHelpAccordion,
    },
    data: () => {
      return {      
        access: {
          module_id: Modules.REPORTES,
          profile_id: Profiles.PERSONAL,
          view_reference: 'design',
          elements: {}
        }, 
        crud: { 
          form: {
            id: 0,     
            reports: null,     
            content: '',         
            type_print: 'pdf',
            orientation: 'portrait',
            page: 'a4',
            font: 'Courier',
            blocked: false,
            name: '',
            active: true,
          },
        },
        filter: {
          id: 0,
        },
        jsonFilter: null,
        contentEscpos: '',
        contentRender: '[[no-report]]',
        pdfPrintReport: '',
        // Medidas de hoja: ÚNICO origen es el backend (GET reports/page-sizes →
        // PageSizes). Nada hardcodeado. `pageSizesMm` ({ code: { w, h } } en
        // mm/portrait) alimenta el cálculo de la grilla de etiquetas; `arr.page`
        // alimenta el combo. Hasta que responde la API, ambos están vacíos y la
        // UI muestra "Cargando…".
        pageSizesMm: {},
        pageSizesLoaded: false,
        arr: {
          orientation: [
            { text: 'Vertical', value: 'portrait' },
            { text: 'Horizontal', value: 'landscape' },
          ],
          page: [],
          fonts: [
            { text: 'Courier', value: 'courier' },
            { text: 'Arial', value: 'arial' },
            { text: 'Helvetica', value: 'helvetica' },
            { text: 'Liberation Sans', value: 'liberation sans' },
            { text: 'Times-Roman', value: 'times-Roman' },
            { text: 'Sans Serif', value: 'sans-serif' },
            { text: 'Symbol', value: 'symbol' },            
          ],
        }
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
      this.loadPageSizes()
    },
    computed: {
      // Los reportes sin módulo activo (ej. Informe de Servicios sin ese
      // módulo contratado) no deben listarse acá — ver ReportsController::
      // controlByFilter en el backend, que cruza esto contra m_reports.module_id.
      activeModuleIds() {
        const modules = Session.getSession().auth.user.permissions.modules || []
        return modules.map(module => module.id)
      },
      isVersionAlternative() {
        let status = false
        if(this.crud.form.reports && this.crud.form.reports.alternative) {
          status = this.crud.form.reports.alternative
        }
        return status
      },
      isLabelReport() {
        return this.crud.form.reports && this.crud.form.reports.reference === 'products-label'
      },
      canEditPdf() {
        return this.crud.form.type_print == 'pdf' || this.crud.form.type_print == 'both'
      },
      canEditEscpos() {
        return this.crud.form.type_print == 'escpos' || this.crud.form.type_print == 'both'
      },
      variablesUsedPdf() {
        return this.canEditPdf ? this.getVariablesUsedByContent(this.crud.form.content) : null
      },
      variablesUsedEscpos() {
        return this.canEditEscpos ? this.getVariablesUsedByContent(this.contentEscpos) : null
      }
    },
    methods: {
      // Medidas de hoja: ÚNICO origen es el backend. Llena el combo "Medida de
      // hoja" y el mapa mm que usa el cálculo de la grilla de etiquetas. Hasta
      // que responde, `pageSizesLoaded` es false y la UI muestra "Cargando…".
      loadPageSizes() {
        serviceAPI.obtenerMedidasHoja()
          .then((response) => {
            const list = Array.isArray(response.data) ? response.data : []
            this.arr.page = list.map(size => ({ text: size.label, value: size.code }))
            const map = {}
            list.forEach(size => {
              map[size.code] = { w: Number(size.width_mm), h: Number(size.height_mm) }
            })
            this.pageSizesMm = map
            this.pageSizesLoaded = list.length > 0
          })
          .catch(error => {
            this.$awn.alert(Error.showError(error))
          })
      },
      loadReports(object) {  
        this.clearReportsAll()          

        if(object){          
          this.crud.form.reports = object          
          this.crud.form.id = object.id  

          this.crud.form.orientation = object.orientation
          this.crud.form.page = object.page
          this.crud.form.font = object.font
          this.crud.form.blocked = object.blocked
          this.crud.form.name = object.name
          this.crud.form.active = object.active
          this.crud.form.type_print = object.type_print || 'pdf'

          if(object.content_user) {
            this.crud.form.content = object.content_user            
            this.contentRender = '[[no-data]]'
          } else {
            this.crud.form.content = object.content     
            this.contentRender = '[[no-config]]'
          }              
        } else {
          this.clearReportsAll()          
        }        
      },
      reloadSelectedReport() {
        if(!this.crud.form.id) {
          return false
        }

        return serviceAPI.obtenerReporte(this.crud.form.id, this.activeModuleIds)
          .then((response) => {
            const report = response.data
            if(!report) {
              this.$awn.alert('No se encontró el reporte actualizado')
              return false
            }

            this.loadReports(report)
            this.filterReports()
            return true
          })
          .catch(error => {
            this.$awn.alert(Error.showError(error))
            return false
          })
      },
      filterReports() {
        if(this.crud.form.reports && !this.crud.form.reports.only_detail) {
          if(!this.filter.id || !this.crud.form.id) {          
            this.clearReportsRender()
            return false          
          }
        } else {
          this.filter.id = 0
        }

        var result = serviceAPI.mostrarJson(this.crud.form.id, this.filter.id)
        
        this.jsonFilter = null
        result.then((response) => {
          var data = response.data
          this.jsonFilter = data

          this.renderReportsPreview()
        })
        .catch(error => {
          this.clearReportsRender()
          this.$awn.alert(this.filter.id ? 'No se encontró el registro ID: ' + this.filter.id : Error.showError(error))
        });
      },
      getVariablesUsedByContent(content) {
        if(!content) {
          return null
        }

        let result = {
          variables: [],
          details: [],
        }

        let matches = content.match(/{{[^}]+}}/g)

        if(!matches || !matches.length) {
          return null
        }

        let inArray = false
        matches.forEach((value) => {
          let isArrayStart = value.indexOf('arrayStart') !== -1
          let isArrayEnd = value.indexOf('arrayEnd') !== -1

          if(isArrayStart || inArray) {
            result.details.push(value)
          } else {
            result.variables.push(value)
          }

          if(isArrayStart) {
            inArray = true
          }

          if(isArrayEnd) {
            inArray = false
          }
        })

        return result
      },
      formatJson(data) {
        return JsonHighlight.formatJson(data)
      },
      renderReports() { 
        if(this.crud.form.reports && !this.crud.form.reports.only_detail) {       
          if(!this.filter.id || !this.crud.form.id) {          
            this.contentRender = '[[no-data]]'
            return false          
          }
        } else {
          this.filter.id = 0
        }

        var result = serviceAPI.mostrarRender(this.crud.form.id, this.filter.id)

        result.then((response) => {
          var data = response.data 
          this.contentRender = data
        })
        .catch(error => {
          this.clearReportsRender()
          this.$awn.alert(this.filter.id ? 'No se encontró el registro ID: ' + this.filter.id : Error.showError(error))
        });                
      },
      renderReportsPreview() {
        if(this.canEditPdf) {
          this.renderReports()
        }

      },
      saveReports(format) {
        if(!this.crud.form.id) {
          this.$awn.alert('No se seleccionó ningun reporte')
          return false          
        }

        let loader = this.$loading.show();      
        let data = Object.assign({}, this.crud.form, { format: format })

        data.blocked = true
        this.crud.form.blocked = true

        var result = serviceAPI.editar(data);
        
        result.then(() => {
          loader.hide()
          this.reloadSelectedReport()
          this.$awn.success("Datos guardados con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      saveReportsConfiguration(format) {
        if(!this.crud.form.id) {
          this.$awn.alert('No se seleccionó ningun reporte')
          return false
        }

        let loader = this.$loading.show();
        let data = Object.assign({}, this.crud.form, { format: format })
        var result = serviceAPI.editarConfiguration(data);
        
        result.then(() => {
          loader.hide()
          this.reloadSelectedReport()
          this.$awn.success("Datos guardados con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      restoreDefaultReports(format) {
        if(!this.crud.form.id) {
          this.$awn.alert('No se seleccionó ningun reporte')
          return false          
        }

        this.$bvModal.msgBoxConfirm('¿Desea restaurar la versión original del reporte?', {
          title: 'Restaurar Reporte',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'warning',
          okTitle: 'Restaurar',
          cancelVariant: 'outline-warning',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          headerBgVariant: 'warning',
          headerTextVariant: 'black',
          hideHeaderClose: false,
          centered: false
        })
        .then(value => {
          if (value) {            
            let loader = this.$loading.show();        

            this.crud.form.content = this.crud.form.reports.content;

            let data = Object.assign({}, this.crud.form, { format: format })
            var result = serviceAPI.editar(data);
            
            result.then(() => {
              loader.hide()
              this.reloadSelectedReport()
              
              this.$awn.success("Reporte restaurado");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })

          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })          
      },
      getFormatContentHTML() {   
        if( this.contentRender) {
          const reference = (this.crud.form.reports && this.crud.form.reports.reference) || ''
          if(!/^invoice-/.test(reference)) return this.contentRender

          // Los comprobantes fiscales usan footer fijo para que Dompdf lo
          // ubique al pie de la hoja. En un iframe corto el navegador toma
          // ese alto como página y superpone el footer sobre el detalle.
          // Este override existe solo en srcdoc: no modifica el HTML enviado
          // al generador PDF ni su posición impresa.
          const previewStyle = '<style>@media screen { footer { position: static !important; bottom: auto !important; left: auto !important; right: auto !important; } main > .page_break ~ * { display: none !important; } }</style>'
          return this.contentRender.replace(/<\/head>/i, previewStyle + '</head>')
        } else {
          return '[[no-config]]'
        }        
      },
      hasDefaultDesign() {
        if(!this.crud.form.reports) {
          return false
        }

        return this.crud.form.reports.content !== undefined && this.crud.form.reports.content !== ''
      },
      generateReports() {
        if(this.crud.form.reports && !this.crud.form.reports.only_detail) {       
          if(!this.filter.id || !this.crud.form.id) {
            this.contentRender = '[[no-data]]'
            this.$awn.alert('No se encontraron datos para imprimir')
            return false          
          }
        } else {
          this.filter.id = 0
        }
        
        var result = serviceAPI.generate({
          reportsID: this.crud.form.id,
          regID: this.filter.id
        })

        result.then((response) => {
          var data = response.data      
          this.pdfPrintReport = data            
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        });             
      },
      clearReportsAll() {
        this.filter.id = 0
        this.jsonFilter = null
        this.contentEscpos = ''
        this.contentRender = '[[no-report]]',
        this.pdfPrintReport = ''

        this.crud.form.reports = null
        this.crud.form.id = 0
        this.crud.form.orientation = 'portrait'
        this.crud.form.page = 'a4'
        this.crud.form.font = 'Courier'
        this.crud.form.content = ''
        this.crud.form.type_print = 'pdf'
        this.crud.form.blocked = false
      },
      clearReportsRender() {
        this.jsonFilter = null
        this.contentRender = '[[no-data]]',
        this.pdfPrintReport = ''
      }
    } 
  }
</script>
<style>
  .reports-design-workspace {
    padding: 0;
    margin-bottom: 20px;
  }
  .reports-design-selector-panel {
    background: var(--app-color-surface);
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    box-shadow: var(--app-shadow-sm);
  }
  .reports-design-selector-panel {
    padding: 14px 16px;
  }
  .reports-design-empty-card {
    margin-top: 16px;
  }
  .reports-design-layout {
    margin-top: 16px;
  }
  .reports-design-layout > [class*="col-"] > .tabs {
    height: 735px;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .reports-design-layout > [class*="col-"] > .tabs > .tab-content {
    min-height: 0;
    flex: 1 1 auto;
    overflow: auto;
  }
 .crud-reports-design-preview {
    background: var(--app-color-canvas);
    padding: 15px;
    height: 590px;
  }
  .crud-reports-design-preview .crud-reports-design-preview-html {
    background: #fff;
    border: 0;
    display: block;
    height: 560px;
    width: 100%;
  }
  .reports-design-accordion {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
  }
  .reports-design-accordion-card {
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    overflow: hidden;
  }
  .reports-design-accordion-header {
    align-items: center;
    color: var(--app-color-text);
    cursor: pointer;
    display: flex;
    font-size: 14px;
    font-weight: 700;
    gap: 6px;
    padding: 10px 14px;
    text-transform: uppercase;
    user-select: none;
  }
  .reports-design-accordion-header:hover {
    background: var(--app-color-primary-soft);
  }
  .reports-design-accordion-chevron {
    margin-left: auto;
    transition: transform 0.2s;
  }
  .collapsed .reports-design-accordion-chevron {
    transform: rotate(-90deg);
  }
  .reports-design-accordion-card .card-body {
    padding: 16px;
  }
  .crud-reports-design-code-editor {
    height: 575px;
  }
  .crud-reports-design-code-editor pre {
    height: 575px;
  }  
  .reports-design-button-filter {
    margin-top: 30px;
  }
  .reports-design-pre-json {
    overflow: auto;    
    height: 625px;
    width: 100%;
    min-width: unset;
  }
  .reports-design-json-code {
    background: var(--app-color-surface-subtle);
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    color: var(--app-color-text);
    font-family: Courier, monospace;
    font-size: 12px;
    line-height: 1.45;
    margin: 0;
    padding: 12px;
    white-space: pre;
  }
  /* Resaltado de sintaxis JSON (por tipo de dato): excepción documentada en
     docs/design-system/color-palette/README.md ("colores monoespaciados o
     resaltado de código, si pertenecen a un editor"). */
  .reports-json-key {
    color: #005cc5;
    font-weight: 700;
  }
  .reports-json-string {
    color: #22863a;
  }
  .reports-json-number {
    color: #b31d28;
  }
  .reports-json-boolean {
    color: #6f42c1;
    font-weight: 700;
  }
  .reports-json-null {
    color: #6a737d;
    font-style: italic;
  }
  .reports-json-accordion {
    background: var(--app-color-surface-subtle);
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    color: var(--app-color-text);
    font-family: Courier, monospace;
    font-size: 12px;
    line-height: 1.45;
    padding: 10px;
  }
  .reports-json-accordion .reports-json-accordion {
    border: 0;
    border-radius: 0;
    background: transparent;
    padding: 0;
  }
  .reports-json-node {
    margin: 2px 0;
  }
  .reports-json-node summary {
    cursor: pointer;
    padding: 4px 2px;
    user-select: none;
  }
  .reports-json-node summary:hover {
    background: var(--app-color-primary-soft);
  }
  .reports-json-summary {
    color: var(--app-color-text-muted);
    margin-left: 6px;
  }
  .reports-json-children {
    border-left: 1px solid var(--app-color-border);
    margin-left: 8px;
    padding-left: 12px;
  }
  .reports-json-leaf {
    padding: 3px 2px;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .reports-json-separator {
    color: var(--app-color-text-muted);
    margin: 0 4px;
  }
  .reports-design-used-accordion {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 625px;
    overflow: auto;
  }
  .reports-design-used-card {
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    overflow: hidden;
  }
  .reports-design-used-header {
    display: flex;
    align-items: center;
    gap: 6px;
    border: 0;
    border-radius: 0;
    font-weight: 700;
    justify-content: flex-start;
    text-align: left;
  }
  .reports-design-used-card .card-body {
    padding: 10px;
  }
  .reports-design-used-card .reports-design-pre-json {
    height: 500px;
  }
  .reports-design-help {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 8px;
    max-height: 625px;
    overflow: auto;
  }
  .reports-design-help-section {
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    background: var(--app-color-surface);
    padding: 12px;
  }
  .reports-design-help-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 700;
    margin-bottom: 8px;
    color: var(--app-color-text);
  }
  .reports-design-help-toggle {
    cursor: pointer;
    user-select: none;
    justify-content: space-between;
    padding: 4px 2px;
    border-radius: 4px;
    transition: background 0.15s;
  }
  .reports-design-help-toggle:hover {
    background: var(--app-color-primary-soft);
  }
  .reports-design-help-chevron {
    margin-left: auto;
    transition: transform 0.2s;
    flex-shrink: 0;
  }
  .collapsed .reports-design-help-chevron {
    transform: rotate(-90deg);
  }
  .reports-design-help-subtitle {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid var(--app-color-border);
    font-weight: 700;
    color: var(--app-color-text-secondary);
  }
  .reports-design-help-mini {
    border-top: 1px solid var(--app-color-border);
  }
  .reports-design-help-mini__title {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 7px 2px;
    border: 0;
    background: transparent;
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-sm);
    font-weight: 700;
    text-align: left;
  }
  .reports-design-help-mini__title:hover { color: var(--app-color-primary); }
  .reports-design-help-mini__title svg { font-size: 16px; }
  .reports-design-help-mini__title.collapsed svg { transform: rotate(-90deg); }
  .reports-design-help-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 0;
    border-top: 1px solid var(--app-color-border);
  }
  .reports-design-help-item code {
    width: fit-content;
    white-space: normal;
    display: inline-block;
    padding: 3px 6px;
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-text);
    font-size: 12px;
    line-height: 1.35;
  }
  .reports-design-help-format-list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
    margin-top: 10px;
  }
  .reports-design-help-format {
    display: grid;
    grid-template-columns: minmax(95px, auto) 1fr;
    gap: 8px;
    align-items: start;
    padding: 6px 0;
    border-top: 1px solid var(--app-color-border);
  }
  .reports-design-help-format code {
    width: fit-content;
    padding: 3px 6px;
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-text);
    font-size: 12px;
    line-height: 1.35;
  }
  .reports-design-help-note {
    margin-top: 8px;
    padding: 8px;
    border-left: 3px solid var(--app-color-info);
    background: var(--app-color-info-soft);
    color: var(--app-color-primary-hover);
  }
</style>
